import { IFormControl } from '../../../ui/forms/interfaces';
import { PaymentTabsEnum } from './enums/payment-tabs-enum';
import { ConfirmOrderResultErrorCodeEnum } from './enums/confirm-order/confirm-order-result-error-enum';
import { TaxIdStatusEnum } from './enums/tax-id-status-enum';
import { CompleteOrderResultErrorCodeEnum } from './enums/confirm-order/complete-order-result-error-code-enum';
import { LastPaymentOption } from './enums/last-payment-option-enum';
import { IUpsellExcludedProduct, IUpsellRule } from '../../../domain/upsell/interfaces';

declare global {
    interface Window {
        // add BridgerPay API to the global window scope
        BP: any;
    }
}

// ------------------- CART DATA ---------------------------------

export interface ICartItemPrices {
    cartItemGUID: string,
    price: number,
    subtotal: number,
    discount: number,
    priceAfterDiscount: number,
    showMSRPPrice: boolean,
    appliedCoupons: string[],
    showInstantSavings: boolean,
    taxPrice: number,
}

export interface IPriceData {
    totalPrice: number;
    subTotalPrice: number;
    totalDiscountPrice: number;
    cartItemPrices: ICartItemPrices[];

    // used in UI to print "Instant Savings"
    // above the "Proceed to checkout" button
    totalUpsellDiscount: number;

    totalTaxPrice: number;
    taxWithoutShippingTax: number;
    shippingTax: number;
    shipping: number;

    collectTax: boolean;

    referCredit: number;
    selectedUserCredit: number;
}

export interface ICartItem {
    /* SP properties */
    cartItemGUID: string,
    nodeGUID: string,
    skU_MSRP: number,
    skuPrice: number,
    wavesCartItemPrice: number,
    skuEnabled: boolean,
    skuNeedsShipping: boolean,
    skuShippingUSCanada: boolean,
    skuid: number,
    skuguid: string,
    skuNumber: string,
    skuName: string,
    skuUnits: number,
    skuThumb: string,
    documentUrlPath: string,
    cartComment: string,
    cartNotification: string,

    skuDepartmentID: number,
    departmentName: string,
    departmentDisplayName: string,

    specialSKUGUID: string,
    specialSKUType: string,

    /* computed properties */
    priceData: ICartItemPrices,
    isQtyControl: boolean,
    isActive: boolean,
    cartGroupID: number,
    isSaveForLater: boolean,

    targetWUPEndDate?: Date|null,
    sbsPeriod?: string;
    numberOfFreeDays?: number;

    bonusWCSCredits: number;
}

export interface ISubscription {
    futureMonthlyPayments: number,
    numberOfFreeDays: number,
    sbsPeriod: string,
}

export interface Coupon{
    couponDiscount?: number;
    message?: string;
    originalCoupon: string;
    duration: number;
}

export interface IEFSData{
    cap: number,
    upperCap: number,
    minNumberOfPurchasedPlugins: number,
    numberOfFreePlugins: number,
}

export interface IInAppPurchaseResult {
    statusCode: number,
}

export interface IMaxMindAddress {
    countryCode?: string;
    stateCode?: string;
    cityName?: string;
}

export interface ICartData {
    email: string;
    cartItems: ICartItem[];
    shoppingCartAppID: number;
    priceData: IPriceData;
    availableCredit: number;

    // coupons
    couponBoxState: number;
    redeemedCoupons: Coupon[];
    errorCoupons: Coupon[];

    // addresses
    billingAddress?: IAddress;
    shippingAddress?: IAddress;

    // free plugins (green messages)
    efsData: IEFSData[];
    efsCampaignTypeID: number;
    efstcurl: string;
    efsFreePluginURL: string;

    inAppPurchaseResult?: IInAppPurchaseResult;
    isShippingNeeded: boolean;

    cartGroupID: number;
    cartGroups: ICartGroup[];

    skipStep1: boolean;
    isAuthenticated: boolean;
    creditCardService: number;

    youMayAlsoLikeData: IYouMayAlsoLikeItem[];
    wishListData: IWithListItem[];

    freeWithPurchaseValue: number;
    freeWithPurchaseType: number;

    subscription?: ISubscription;

    // marketing scripts & pixels
    addedProductSKUID: number;
    facebookAddToCartGuid: string;
    isReferFriendEnabled: boolean;

    // https://wavesaudio.atlassian.net/browse/DSWEB-3278
    // ap = alipay, pp = paypal, cc = credit card, null = no previous purchase
    lastPaymentOption: LastPaymentOption;

    // https://wavesaudio.atlassian.net/browse/DSWEB-3281
    // https://dev.maxmind.com/geoip/docs/web-services/requests
    // https://github.com/maxmind/GeoIP2-dotnet/tree/main
    maxMindAddress: IMaxMindAddress;

    // https://wavesaudio.atlassian.net/browse/DSWEB-4332
    numberOfCreditCards: number;

    // https://wavesaudio.atlassian.net/browse/DSWEB-4475
    validationCode: CartDataValidationCode;

    // upsell -  https://wavesaudio.atlassian.net/browse/MRKT-28959
    upsellTCURL: string;
    upsellTypeID: number;
    upsellRules: IUpsellRule[];
    upsellExcludedProducts: IUpsellExcludedProduct[];
}

export enum CartDataValidationCode {
    NoError = 0,
    EFSError = 1,
    WavesUpdatePlanError = 2,
    UpgradeError = 3,
    CreditsEqualPlansError = 4,
    CreditsEqualPlansBuyCreditsError = 5,
    CreditsUpgradeWarning = 6,
    CreditsBiggerPlanError = 7,
    AlreadySubscribedToWCA = 8,
}

export interface IYouMayAlsoLikeItem {
    documentID: number,
    documentName : string,
    skuid: number,
    thumb : string,
    documentUrlPath : string,
    couponPrice: number,
    skuPrice: number,
    skU_MSRP: number,
    skuNumber: string,
    couponCode : string,
    couponCampaignID: number;
    discount: number,
    rating: number,
    reviewsTotal: number,
    saleEndDate: Date,
    score: number,
}

export interface IWithListItem {
    documentID: number,
    wishlistID: number,
    wishlistItemID: number,
    documentName: string,
    thumb: string,
    documentUrlPath: string,
    skuid: number,
    skuPrice: number,
    skU_MSRP: number,
    couponPrice: number,
    couponCode: string,
    discount: number,
    couponCampaignID: number,
    category: string,
}

export interface IExtraCartData {
    isShippingDifferentFromBilling?: boolean,
}

// additional data that is passed to each 'get cart data' request
// to retrieve the correct tax and shipping prices
export interface ILocalDataRequest {
    taxCountry2LetterCode?: string,
    taxState2LetterCode?: string,
    taxZipCode?: string,
    taxID?: string,

    shipAddressLine?: string,
    shipCity?: string,
    shipCountry2LetterCode?: string,
    shipState2LetterCode?: string,
    shipZipCode?: string,
    shippingCode?: string,

    step?: number,
}

export interface IUndoCartItem {
    skuid: number,
    name: string,
    quantity: number,
}

export interface ICartGroup {

    // server side fields
    cartGroupID: number,
    itemOrder: number,
    groupName: string,
    groupNote: string,
    isMultiProduct: boolean,
    isSkipStep1: boolean,

    // computed fields
    cartItems?: ICartItem[],
}

// ------------------- BrigerPay & PAYMENT METHODS ---------------------------------

export interface ICreditCard {
    kenticoItemID: number;
    type: string; // singleUse
    token: string; // "488b0f70-e3c7-49bb-8046-809f67c51c6f",
    customer_id: string; // "111",
    card_checksum?: string;
    card: {
        masked_number: string; // "545454******5454",
        bin: string; // "54545454",
        last_4_digits: string; // "5454", old - last4Digits
        expire_month: number; // 12, old: expirationDate
        expire_year: number; // 2025, old: expirationDate
        holder_name: string;
        brand: string; // "mastercard",
        type: string; // "credit", old - cardType: string, // 'DEBIT'
        issuer: string; // "central trust bank, the",
        country: string; // "US"
    },

    // Encrypted CVV is received from kentico DB.
    // If it doesn't exist ---> show the cvv textbox.
    encrypted_cvv?: string;

    /*
    holderName: string,
    token: string,
    vendor: string,
    paymentMethod: PaymentMethodEnum,*/
}

export interface IBrigerPayValidation {
    dirty: boolean;
    empty: boolean;
    error: string;
    invalid: boolean;
    pristine: boolean;
    touched: boolean;
    untouched: boolean;
    valid: boolean;
}

export interface IConfirmOrderSavedCreditCardValidation {
    isValid: boolean;
    savedCreditCardToken: string;
    encryptedCvv: string;
    savedCreditCardKenticoItemID: number;
    cvv: string;
    cardChecksum: string;
}

export interface IConfirmOrderNewCreditCardValidation {
    isValid: boolean;
    newCreditCardToken: string;
    encryptedCvv: string;
    cvv: string;
    serverError?: string;
    cardType: string;
    cardChecksum: string;
}

// ------------------- ADDRESS & SESSION DATA ---------------------------------

/**
 * confirm order view model interface;
 * this model will be passed to 'confirm order',
 * and also it will be saved to session storage
 * and extracted from there when needed
 */
export interface IAddress {
    addressID?: number,
    addressGUID?: string,
    addressPersonalName: string,
    addressLine1: string,
    addressLine2: string,
    addressCountryID: number,
    addressStateID: number,
    addressCity: string,
    addressZip: string,
    phoneNumber: string,
    addressPhone?: string;
    phoneCountryID: number,

    // tax id: https://wavesaudio.atlassian.net/browse/DSWEB-6
    // used in billing address only
    taxID?: string,
    taxIDSelectedStatus?: TaxIdStatusEnum,

    // the below fields are used to print data on thank you page
    countryDisplayName?: string,
    stateCode?: string,
}

/**
 * cart data that saved to session storage
 */
export interface ICartDataSerialized {
    billingAddress: IAddress,
    shippingAddress?: IAddress,
}

export interface IBillingControls {
    billToControl: IFormControl,
    addressLine1Control: IFormControl,
    cityControl: IFormControl,
    countryIdControl: IFormControl,
    stateIdControl: IFormControl,
    phoneControl: IFormControl,
    phoneCountryIdControl: IFormControl,

    taxIdControl: IFormControl,
    taxIdSelectedStatusControl: IFormControl,
}

export interface IShippingControls {
    shipToControl: IFormControl,
    addressLine1Control: IFormControl,
    cityControl: IFormControl,
    countryIdControl: IFormControl,
    stateIdControl: IFormControl,
    phoneControl: IFormControl,
    phoneCountryIdControl: IFormControl,
}

export interface ITaxAddressData {
    taxCountry2LetterCode?: string,
    taxState2LetterCode?: string,
    taxZipCode?: string,
}

// ------------------- SHIPPING ---------------------------------

export interface IShippingOption {
    code: string,
    description: string,
    delivery: string,
    error: string,
    totalCharge: number,
    dropdownItemText: string,
    dropdownItemValue: string,
}

export interface IShippingResult {
    code: number,
    text: string,
}

export interface IShippingResponse {
    rates: IShippingOption[],
    result: IShippingResult,
}

// ------------------- CONFIRM ORDER ---------------------------------

export interface IConfirmOrderRequest {
    paymentMethodToken: string,
    encryptedCvv: string,
    cvv: string;
    savedCreditCardKenticoItemID: number;
    paymentType: PaymentTabsEnum,
    cardChecksum: string;

    rememberMe: boolean,
    isNewCreditCard: boolean,

    billingAddress: IAddress,
    shippingAddress?: IAddress,

    // used for 'total changed' validation error
    checksumTotal: number,

    upsShippingCode?: string,
    isShippingDifferentFromBilling: boolean,

    lastUTMSource: string,
    lastUTMMedium: string,
    lastUTMCampaign: string,

    useCreditRecurring: boolean;
}

export interface IConfirmOrderWithoutLeftSideRequest {
    lastUTMSource: string,
    lastUTMMedium: string,
    lastUTMCampaign: string,
}

export interface IConfirmOrderResponse {

    // Validations -------------------
    isValid: boolean;
    errorCode: ConfirmOrderResultErrorCodeEnum;
    insertOrderError: number;
    completeOrderError: CompleteOrderResultErrorCodeEnum,
    zoozErrorMessage: string;
    errorMessage: string;

    cartData?: ICartData;
    orderGUID?: string;

    // Paypal / AliPay ---------------
    redirect: string;

    // ApplePay ----------------------
    applyPayCashierKey?: string;
    applyPayCashierToken?: string;
}


export interface IConfirmOrderWithoutLeftSideResponse {

    // Validations -------------------
    isValid: boolean;
    errorCode: ConfirmOrderResultErrorCodeEnum;
    insertOrderError: number;
    completeOrderError: CompleteOrderResultErrorCodeEnum,
    zoozErrorMessage: string;
    errorMessage: string;

    cartData?: ICartData;
    orderGUID?: string;

    // Paypal / AliPay ---------------
    redirect: string;
}
